/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W4 — PLATE HEAT EXCHANGER CMS-PATH RESOLVER (§33.4, §31).
 *
 * ★ REUSE SEAM (uvcCms.ts §32): the universal CMS-doc accessor
 * `getFromCmsData(doc, dot-path, id)` — a verbatim port of legacy
 * `fn.tools.get_from_cms_data` — lives in uvcCms.ts and was flagged as the W3/W4
 * reuse seam. This module IMPORTS it and adds the phx path-map + label rules.
 * Nothing is duplicated (same pattern energyWheelCms used for W3).
 *
 * The PHX selector resolves a SUBSET of its dependent selects through the phx
 * CMS params doc (plate_heat_exchanger.selector.js:1116-1153
 * PARAMETER_POSSIBLE_VALUES_PATHS_MAPPING — only the `plate_heat_exchanger.*`
 * dotted strings; the `<field>.data` entries are MASTER lists loaded by the
 * dependency graph, NOT the CMS doc):
 *   flow_type_id                 → plate_heat_exchanger.flow_type
 *   type_desired_efficiency_id   → plate_heat_exchanger.type_desired_efficiency
 *   plate_spacing_code_id        → plate_heat_exchanger.spacing_code
 *   face_one/two_definition_id   → plate_heat_exchanger.face_definition
 *   bypass_location_id           → plate_heat_exchanger.bypass.location
 *   bypass_type_id               → plate_heat_exchanger.bypass.type
 *   bypass_side_id (dmr_side)    → plate_heat_exchanger.bypass.dmr_side
 *   bypass_dmr_config_id         → plate_heat_exchanger.bypass.dmr_config
 *   bypass_dmr_location_id       → plate_heat_exchanger.bypass.dmr_location
 *   bypass_dmr_drive_location_id → plate_heat_exchanger.bypass.dmr_drive_location
 *   dmrs_supplied_by_id          → plate_heat_exchanger.bypass.dmrs_supplied_by
 *   recirc_dmr_config_id         → plate_heat_exchanger.recirc_dmr.config
 *   recirc_dmr_location_id       → plate_heat_exchanger.recirc_dmr.location
 *
 * Every OTHER dependent select (temp_rating/seal/plate/end_plate/corner_profile
 * mtl+finish+style, mounting, plate_orientation, configuration, certification,
 * bypass_dmr_mtl/fin) comes from the MASTER-list branch (PHX_PARAMETER_NAMES) —
 * those are NOT doc-resolved; they render from the dependency-graph option lists.
 *
 * The doc is fetched once via the shared getComponentCmsDoc RTKQ query. NB the
 * raw doc is WRAPPED under the partName key {"plate_heat_exchanger": {...}}; the
 * paths INCLUDE the `plate_heat_exchanger.` prefix so they walk the raw doc, but
 * we also tolerate an already-unwrapped doc (strip the leading segment then).
 */

import { getFromCmsData, type UvcCmsDoc } from './uvcCms';
import type { CmsNamedRow } from '../cmsDoc';
import type { OptionRow } from '../formKit';

export type PhxCmsDoc = UvcCmsDoc;

/** field-name → CMS doc dot-path (the `plate_heat_exchanger.*` subset only). */
export const PHX_CMS_PATHS: Record<string, string> = {
  flow_type_id: 'plate_heat_exchanger.flow_type',
  type_desired_efficiency_id: 'plate_heat_exchanger.type_desired_efficiency',
  plate_spacing_code_id: 'plate_heat_exchanger.spacing_code',
  face_one_definition_id: 'plate_heat_exchanger.face_definition',
  face_two_definition_id: 'plate_heat_exchanger.face_definition',
  bypass_location_id: 'plate_heat_exchanger.bypass.location',
  bypass_type_id: 'plate_heat_exchanger.bypass.type',
  bypass_side_id: 'plate_heat_exchanger.bypass.dmr_side',
  bypass_dmr_config_id: 'plate_heat_exchanger.bypass.dmr_config',
  bypass_dmr_location_id: 'plate_heat_exchanger.bypass.dmr_location',
  bypass_dmr_drive_location_id: 'plate_heat_exchanger.bypass.dmr_drive_location',
  dmrs_supplied_by_id: 'plate_heat_exchanger.bypass.dmrs_supplied_by',
  recirc_dmr_config_id: 'plate_heat_exchanger.recirc_dmr.config',
  recirc_dmr_location_id: 'plate_heat_exchanger.recirc_dmr.location',
};

/** Resolve the dot-path against a doc that may be wrapped OR unwrapped: if the
 * leading `plate_heat_exchanger` segment isn't present at the doc root, drop it
 * (the form's componentCms is already unwrapped). */
function resolvePath(doc: PhxCmsDoc | undefined, path: string): string {
  if (!doc) return path;
  const head = path.split('.')[0];
  if (head in (doc as Record<string, unknown>)) return path;
  // unwrapped doc → strip the leading partName segment.
  if (head === 'plate_heat_exchanger') return path.split('.').slice(1).join('.');
  return path;
}

/** The option <text> for a phx CMS row (name, with value/series fall-through —
 * the legacy phx selects render `{{name}}`). DISPLAY ONLY. */
export function phxCmsOptionLabel(row: CmsNamedRow | undefined): string {
  if (!row) return '';
  if (row.name != null && row.name !== '') return String(row.name);
  if (row.value != null && row.value !== '') return String(row.value);
  if (row.series != null && row.series !== '') return String(row.series);
  return '';
}

/** The RATE-INPUT code for a phx CMS row — the VENDOR code, not the display
 * label. Legacy plate_heat_exchanger.selector.js:1170-1181 sets
 * dataForRate[i] = itemValue.value ?? itemValue.name ?? itemValue.series (vs
 * dataForPrintRate[i] = itemValue.name). The vendor keys on `.value`
 * ("wet_effectiveness"/"l"/"external_right_a"), NOT the human label. */
export function phxCmsOptionRateValue(
  row: CmsNamedRow | undefined
): string | undefined {
  if (!row) return undefined;
  if (row.value != null && row.value !== '') return String(row.value);
  if (row.name != null && row.name !== '') return String(row.name);
  if (row.series != null && row.series !== '') return String(row.series);
  return undefined;
}

/** Is this field resolved via the phx CMS doc (vs a master list / model field)? */
export function isPhxCmsField(field: string): boolean {
  return field in PHX_CMS_PATHS;
}

/** OPTION LIST for a doc-resolved phx select. ids==null ⇒ FULL list. */
export function phxCmsOptionList(
  doc: PhxCmsDoc | undefined,
  field: string,
  ids?: Array<number | string> | null
): OptionRow[] {
  const path = PHX_CMS_PATHS[field];
  if (!path) return [];
  const result = getFromCmsData(doc, resolvePath(doc, path), ids ?? null);
  const arr = Array.isArray(result) ? result : result ? [result] : [];
  return arr.map((row) => ({
    ...row,
    id: row.id,
    label: phxCmsOptionLabel(row),
  })) as OptionRow[];
}

/** DISPLAY LABEL for the currently-selected id of a doc-resolved phx select.
 * Use this ONLY for what the user sees (the select display) — NOT the rate
 * input. The rate input must use phxCmsSelectedValue (BUG 1). */
export function phxCmsSelectedLabel(
  doc: PhxCmsDoc | undefined,
  field: string,
  id: number | string | null | undefined
): string {
  if (id == null || id === '') return '';
  const path = PHX_CMS_PATHS[field];
  if (!path) return '';
  const row = getFromCmsData(doc, resolvePath(doc, path), id);
  return phxCmsOptionLabel(Array.isArray(row) ? row[0] : row);
}

/** RATE-INPUT VENDOR CODE for the currently-selected id of a doc-resolved phx
 * select. Returns the row `.value` (vendor code) — NOT the display `.name`
 * (BUG 1: the vendor rejected "Wet Effectiveness"/"L"/"External right : A"; it
 * wants "wet_effectiveness"/"l"/"external_right_a"). Returns undefined when the
 * doc has no matching row so the caller can fall back to the raw id. */
export function phxCmsSelectedValue(
  doc: PhxCmsDoc | undefined,
  field: string,
  id: number | string | null | undefined
): string | undefined {
  if (id == null || id === '') return undefined;
  const path = PHX_CMS_PATHS[field];
  if (!path) return undefined;
  const row = getFromCmsData(doc, resolvePath(doc, path), id);
  return phxCmsOptionRateValue(Array.isArray(row) ? row[0] : row);
}
